import { useEffect, useMemo, useState } from 'react'
import { Breadcrumb, Button, Card, Col, Form, InputNumber, notification, Row, Switch, Typography } from 'antd'
import { HomeOutlined, LeftCircleOutlined, SaveOutlined } from '@ant-design/icons'
import { useHistory } from 'react-router-dom'
import { useStore } from 'src/hooks'
import FloatLabel from 'src/common/components/float-label'
import { useTranslation } from 'react-i18next'
import i18n from 'src/helpers/i18n'
import { createValidationRules } from 'src/helpers/validateRule'
import { observer } from 'mobx-react-lite'
import { useLoading } from 'src/common/components/LoadingContainer'

const { Title } = Typography

const CommonConfigScreen = observer(() => {
   const history = useHistory()
   const [form] = Form.useForm()
   const CommonConfigStore = useStore('CommonConfigStore')
   const { t } = useTranslation()
   const { showLoading, hideLoading } = useLoading()
   const [configData, setConfigData] = useState<any>({})

   useEffect(() => {
      getConfigDesigner()
   }, [])

   const getConfigDesigner = async () => {
      showLoading()
      const data = await CommonConfigStore.getConfigCommon()
      if (data) {
         setConfigData(data)
         form.setFieldsValue({
            ...data,
            AUTO_PUSH: data.AUTO_PUSH === '1',
            IS_PRICE_INCREASE: data.IS_PRICE_INCREASE === '1'
         })
      }
      hideLoading()
   }

   const rule = useMemo(() => createValidationRules(), [i18n.language])

   const onSave = async () => {
      try {
         const values = await form.validateFields()
         const payload = {
            ...values,
            AUTO_PUSH: values.AUTO_PUSH ? '1' : '0',
            IS_PRICE_INCREASE: values.IS_PRICE_INCREASE ? '1' : '0'
         }

         showLoading()
         const result = await CommonConfigStore.updateConfigCommon(payload)
         hideLoading()

         if (result) {
            setConfigData(payload)
            notification.success({
               message: t('notification'),
               description: t('update_successfully')
            })
            // history.goBack()
         }
      } catch (error) {
         console.log('Validation failed:', error)
      }
   }

   const renderContent = () => (
      <Card style={{ width: '100%', marginTop: 20 }}>
         <Row gutter={24} className="gx-flex-row">
            {/* Cột trái: Điểm Hoa hồng mặc định */}
            <Col xs={24} md={12}>
               <Title level={5}>Điểm Hoa hồng mặc định</Title>
               {[
                  { name: 'HH_GHEP_1', label: '1 ghế - ghép' },
                  { name: 'HH_GHEP_2', label: '2 ghế - ghép' },
                  { name: 'HH_GHEP_3', label: '3 ghế - ghép' },
                  { name: 'HH_BAO_XE_5', label: 'Bao xe 5' },
                  { name: 'HH_BAO_XE_7', label: 'Bao xe 7' },
                  { name: 'HH_KHU_HOI', label: 'Khứ hồi' },
                  { name: 'HH_GUI_HANG_NHO', label: 'Gửi hàng nhỏ' },
                  { name: 'HH_GUI_HANG_LON', label: 'Gửi hàng to' }
               ].map((item) => (
                  <Col key={item.name} span={24}>
                     <FloatLabel label={item.label}>
                        <Form.Item
                           name={item.name}
                           rules={[rule.requiredEnterNumber]}
                           initialValue={Number(configData?.[item.name] || 0)}
                        >
                           <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                     </FloatLabel>
                  </Col>
               ))}
               {/* Công tắc */}
               <Row style={{ marginTop: 20 }}>
                  <Col span={24}>
                     <FloatLabel label="Tự động đẩy đơn sang tài xế">
                        <Form.Item
                           name="AUTO_PUSH"
                           valuePropName="checked"
                           initialValue={configData?.AUTO_PUSH === '1'}
                        >
                           <Switch style={{ marginTop: 16 }} />
                        </Form.Item>
                     </FloatLabel>
                  </Col>
               </Row>
            </Col>

            {/* Cột phải: Giảm giá theo ghế */}
            <Col xs={24} md={12}>
               <Title level={5}>Giảm giá theo ghế</Title>
               <Col span={24}>
                  <FloatLabel label="2 ghế - ghép (theo số tiền)">
                     <Form.Item
                        name="KM_GHEP_2"
                        rules={[rule.requiredEnterNumber]}
                        initialValue={Number(configData?.KM_GHEP_2 || 0)}
                     >
                        <InputNumber min={0} style={{ width: '100%' }} />
                     </Form.Item>
                  </FloatLabel>
               </Col>

               <Col span={24}>
                  <FloatLabel label="3 ghế - ghép (theo %)">
                     <Form.Item
                        name="KM_GHEP_3"
                        rules={[rule.requiredEnterNumber]}
                        initialValue={Number(configData?.KM_GHEP_3 || 0)}
                     >
                        <InputNumber
                           min={0}
                           max={100}
                           formatter={(value) => `${value}%`}
                           parser={(value) => value!.replace('%', '')}
                           style={{ width: '100%' }}
                        />
                     </Form.Item>
                  </FloatLabel>
               </Col>
            </Col>
            {/* Cột phải: Giảm giá theo ghế */}
            <Col xs={24} md={12}>
               <Title level={5}>Cấu hình tăng giá</Title>
               <Col span={24}>
                  <FloatLabel label="Tăng giá theo %">
                     <Form.Item
                        name="PRICE_INCREASE"
                        rules={[rule.requiredEnterNumber]}
                        initialValue={Number(configData?.PRICE_INCREASE || 0)}
                     >
                        <InputNumber
                           min={0}
                           max={100}
                           formatter={(value) => `${value}%`}
                           parser={(value) => value!.replace('%', '')}
                           style={{ width: '100%' }}
                        />
                     </Form.Item>
                  </FloatLabel>
               </Col>
               <Row style={{ marginTop: 20 }}>
                  <Col span={24}>
                     <FloatLabel label="Bật tăng tiền">
                        <Form.Item
                           name="IS_PRICE_INCREASE"
                           valuePropName="checked"
                           initialValue={configData?.IS_PRICE_INCREASE === '1'}
                        >
                           <Switch style={{ marginTop: 16 }} />
                        </Form.Item>
                     </FloatLabel>
                  </Col>
               </Row>
            </Col>
         </Row>
      </Card>
   )

   return (
      <div>
         <Row style={{ padding: '0 20px' }} className="gx-justify-content-between gx-align-items-center">
            <Col>
               <Title level={4}>{'Cấu hình chung'}</Title>
               <Breadcrumb>
                  <Breadcrumb.Item href="/">
                     <HomeOutlined className="gx-link" />
                  </Breadcrumb.Item>
                  <Breadcrumb.Item>{'Admin'}</Breadcrumb.Item>
                  <Breadcrumb.Item onClick={() => history.goBack()} className="gx-link">
                     {'Cấu hình chung'}
                  </Breadcrumb.Item>
               </Breadcrumb>
            </Col>
         </Row>
         <Form form={form} name="commonConfig" layout="vertical" autoComplete="off">
            {renderContent()}
            <Form.Item style={{ width: '100%', textAlign: 'center' }}>
               {/* <Button
                  icon={<LeftCircleOutlined />}
                  type="dashed"
                  style={{ color: '#ED5C19' }}
                  onClick={() => history.goBack()}
                  className="gx-mr-2"
               >
                  {t('txt_back')}
               </Button> */}
               <Button icon={<SaveOutlined />} type="primary" onClick={onSave} className="gx-mr-2">
                  {t('save')}
               </Button>
            </Form.Item>
         </Form>
      </div>
   )
})

export default CommonConfigScreen
